import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useReduxLookups } from "hooks/useReduxLookups";
import useDebounce from "hooks/useDebounce";
import { actions } from "store/actions";
import useNotify from "hooks/useNotify";

const useVendorTasks = () => {
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.userDetails || {});
  const { notifyError } = useNotify();

  const [searchTerm, setSearchTerm] = useState(undefined);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedDeadline, setSelectedDeadline] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const { services } = useReduxLookups(true); // vendor

  const vendorTasks = useSelector(
    (state) => state.vendorTasks || { results: [], count: 0 }
  );

  const serviceOptions = services.map((service) => ({
    ...service,
    value: service.id,
    label: service.title,
  }));

  const [filterQuery, setFilterQuery] = useState({
    filter: { op: "and", value: [] },
    paginate: {
      pageSize: 10,
      pageNumber: 1,
    },
  });

  useEffect(() => {
    if (debouncedSearchTerm !== undefined) {
      setFilterQuery((prevQuery) => {
        const trimmed = debouncedSearchTerm.trim();
        let value = [...prevQuery.filter.value];

        const existing = value.find((item) => item.key === "po_number");
        if (existing) {
          existing.value = trimmed;
        } else if (trimmed) {
          value.push({ op: "lik", key: "po_number", value: trimmed });
        }

        if (!trimmed) {
          value = value.filter((item) => item.key !== "po_number");
        }

        return {
          filter: { ...prevQuery.filter, value },
          paginate: { ...prevQuery.paginate, pageNumber: 1 },
        };
      });
    }
  }, [debouncedSearchTerm]);

  const fetchTasks = useCallback(() => {
    dispatch(
      actions.getVendorTasks(
        userDetails?.vendorId,
        filterQuery,
        (success, err) => {
          setIsLoading(false);
          if (!success) {
            notifyError(
              err?.response?.data?.message ??
                err?.message ??
                "Failed to fetch tasks"
            );
          }
        }
      )
    );
  }, [dispatch, userDetails?.vendorId, filterQuery]);

  useEffect(() => {
    if (userDetails?.vendorId) {
      fetchTasks();
    }
  }, [filterQuery, userDetails?.vendorId]);

  const handlePageChange = useCallback((pageNumber) => {
    setFilterQuery((prevQuery) => ({
      ...prevQuery,
      paginate: { ...prevQuery.paginate, pageNumber },
    }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize) => {
    setFilterQuery((prevQuery) => ({
      ...prevQuery,
      paginate: { pageSize, pageNumber: 1 },
    }));
  }, []);

  const clearFilters = () => {
    setSearchTerm(undefined);
    setSelectedServices([]);
    setSelectedDeadline(null);
    setFilterQuery({
      paginate: { pageSize: 10, pageNumber: 1 },
      filter: { op: "and", value: [] },
    });
  };

  return {
    searchTerm,
    setSearchTerm,

    selectedServices,
    setSelectedServices,
    selectedDeadline,
    setSelectedDeadline,

    serviceOptions,

    tasks: vendorTasks.result,
    totalCount: vendorTasks.count,
    isLoading,

    pagination: filterQuery.paginate,
    handlePageChange,
    handlePageSizeChange,

    clearFilters,
    fetchTasks,
    filterQuery,
    setFilterQuery,
  };
};

export default useVendorTasks;
